import { useMount } from 'react-use';
import React, { startTransition, useCallback, useMemo, useState } from 'react';
import { extractParamsFromUrl, parseSearchParams } from '@pkgs/components/utils/searchParams';
import { DEFAULT_OPTIONS } from '@pkgs/api/utils/filters';
import { CountryCode, type Currency, type Language, MarketCountryCode, type MeasurementSystem } from '@pkgs/api';
import { useRouting } from '@ev-tech/ui-core-components/RoutingProvider';

import { defaultSettingsByLang } from '@/utils/settings';
import { readSettingsFromStorage, writeSettingsToStorage } from '@/utils/localStorage';

export type Settings = {
  currency: Currency;
  measurementSystem: MeasurementSystem;
  countryCode: MarketCountryCode;
};

export type SettingsContextType = Settings & {
  language: Language;
  setSettings: (settings: Partial<Settings>) => void;
};

const initialStore = {
  setSettings: () => null,
  currency: DEFAULT_OPTIONS.currency,
  measurementSystem: DEFAULT_OPTIONS.measurementSystem,
  countryCode: MarketCountryCode.DE,
  language: DEFAULT_OPTIONS.language,
};

export const SettingsContext = React.createContext<SettingsContextType>(initialStore);

export const SettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const { router } = useRouting();
  const { countryCode, language = DEFAULT_OPTIONS.language } = extractParamsFromUrl(router.asPath);
  const { options: urlSettings } = parseSearchParams(router.asPath);

  const [currentSettings, setCurrentSettings] = useState<Settings>({
    ...defaultSettingsByLang[language],
    ...urlSettings,
    countryCode: countryCode ?? CountryCode.DE,
  });

  const setSettings = useCallback(
    (newSettings: Partial<Settings>) => {
      const updatedSettings: Settings = {
        ...currentSettings,
        ...newSettings,
      };

      startTransition(() => {
        setCurrentSettings(updatedSettings);
        writeSettingsToStorage(updatedSettings, language);
      });
    },
    [currentSettings, language],
  );

  useMount(() => {
    const settings = readSettingsFromStorage();

    if (!settings) return;

    const { options: urlSettings } = parseSearchParams(router.asPath);

    if (urlSettings.currency) {
      settings.currency = urlSettings.currency;
    }

    if (urlSettings.measurementSystem) {
      settings.measurementSystem = urlSettings.measurementSystem;
    }

    setSettings(settings);
  });

  const value = useMemo(
    () => ({
      language,
      currency: currentSettings.currency,
      measurementSystem: currentSettings.measurementSystem,
      countryCode: currentSettings.countryCode,
      setSettings,
    }),
    [currentSettings.countryCode, currentSettings.currency, currentSettings.measurementSystem, language, setSettings],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};
